/**
 * Fortunio Points (FP) Rewards Handler
 * 
 * Processes API responses that include FP awards and achievement unlocks.
 * Shows toast notifications for earned FP, guest "could have earned" messages,
 * and achievement unlock notifications.
 */

import { showGuestFPModal } from '../components/guest-modals';
import { logger } from '../lib/logger';
import { processUnlockedAchievements, UnlockedAchievement } from '../components/achievement-toast';
import { balanceService } from '../services/balance-service';

/**
 * API responses can include FP reward information and achievement unlocks.
 */
export interface ApiResponseWithFP {
  fp_earned?: number | null;
  hypothetical_fp?: number | null;
  unlocked_achievements?: UnlockedAchievement[] | null;
}

/**
 * Process API response and show FP and achievement notifications if applicable.
 * 
 * Call this after game completion, score submission, etc.
 * Handles:
 * - Authenticated user FP awards
 * - Guest "could have earned" hypothetical FP
 * - Achievement unlock notifications
 * 
 * @param response - API response object
 * @returns true if any notification was shown
 */
export function handleFPRewards(response: ApiResponseWithFP): boolean {
  let notificationShown = false;
  
  // Refresh navbar FP balance for authenticated users who earned FP
  if (response.fp_earned && response.fp_earned > 0) {
    logger.log('FP earned:', response.fp_earned);
    notificationShown = true;
    
    // Refresh navbar balance from server (server is authority)
    // Badge animator auto-triggers animation via BalanceService subscription
    balanceService.refresh().catch((err: unknown) => {
      logger.error('Failed to refresh FP balance from server:', err);
    });
  }
  
  // Show hypothetical FP message for guests
  if (response.hypothetical_fp && response.hypothetical_fp > 0) {
    logger.log('Guest hypothetical FP:', response.hypothetical_fp);
    void showGuestFPModal(response.hypothetical_fp);
    notificationShown = true;
  }
  
  // Process achievement unlocks (shown after FP notifications with staggered delay)
  if (response.unlocked_achievements && response.unlocked_achievements.length > 0) {
    // Delay achievement notifications slightly to avoid overwhelming user
    // if they just got an FP notification
    const delay = notificationShown ? 1500 : 0;
    setTimeout(() => {
      processUnlockedAchievements(response.unlocked_achievements);
    }, delay);
    notificationShown = true;
  }
  
  return notificationShown;
}

/**
 * Process only achievement unlocks from an API response, skipping FP notifications.
 *
 * Use after game completion when FP is already shown by the end-of-game overlay
 * (FIX-09) but achievement toasts/modals still need to fire.
 *
 * @param response - API response object
 */
export function processAchievementsOnly(response: ApiResponseWithFP): void {
  if (response.unlocked_achievements && response.unlocked_achievements.length > 0) {
    // Delay slightly to let the end-of-game overlay render first
    setTimeout(() => {
      processUnlockedAchievements(response.unlocked_achievements!);
    }, 500);
  }
}

/**
 * Convenience function to check if user is a guest
 * (based on no FP earned but hypothetical FP present)
 */
export function isGuestFromResponse(response: ApiResponseWithFP): boolean {
  return !response.fp_earned && !!response.hypothetical_fp;
}
